import {
    ApplicationRef,
    ComponentRef,
    createComponent,
    EnvironmentInjector,
    inject,
    Injectable,
    Injector,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
    DistributiveOmit as MapLibreDistributiveOmit,
    LayerSpecification as MapLibreLayerSpecification,
    LngLatLike,
    MapGeoJSONFeature,
    MapGeoJSONFeature as MapLibreMapGeoJSONFeature,
    Popup,
} from 'maplibre-gl';

import { MapSelectionPopupComponent } from '@connect-services/client/components/map/map-selection-popup/map-selection-popup.component';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapClickIndicatorService } from '@connect-services/client/services/map-click-indicator.service';
import {
    ActiveElementEnum,
    MapElementsPositionService,
} from '@connect-services/client/services/map-elements-position.service';
import {
    MapModalService,
    ModalInstanceState,
    OpenedModalElement,
} from '@connect-services/client/services/map-modal.service';

export type CreatePopupOptions = {
    eventListeners?: Record<string, EventListener>;
    maxWidth: number;
    onClose?: () => void;
    position: LngLatLike;
};

export type MapGeoJSONFeatureWithLayer = Omit<MapLibreMapGeoJSONFeature, 'layer'> & {
    layer: MapLibreDistributiveOmit<MapLibreLayerSpecification, 'source'> & {
        name: string;
        source: string;
    };
};

export const DEFAULT_MODAL_HEIGHT = 384;
const POPUP_RESERVED_HEIGHT = 180;

const DRAG_Y_ADJUSTMENT = 20;
const EXCLUDED_DRAGGABLE_SELECTORS = ['ul', 'li', 'a', 'button'];
const MAP_POPUP_SELECTOR: string = '.maplibregl-popup-content';
const SELECTED_ITEM_SELECTOR = 'selected-item';

const EDIT_VERTEX_POPUP_WIDTH = '40rem';

@Injectable({
    providedIn: 'root',
})
export class MapPopupService extends MapBaseService {
    private readonly applicationRef = inject(ApplicationRef);
    private readonly environmentInjector = inject(EnvironmentInjector);
    private readonly mapClickIndicatorService = inject(MapClickIndicatorService);
    private readonly mapElementsPositionService = inject(MapElementsPositionService);
    private readonly mapModalService = inject(MapModalService);
    private popup?: Popup;

    public constructor(
        private injector: Injector,
        private translocoService: TranslocoService,
    ) {
        super();
    }

    public closePopup(): void {
        this.mapClickIndicatorService.removeIndicatorSource();

        if (!this.popup) {
            return;
        }

        this.popup.remove();
    }

    public createLayerMarkersPopup(
        features: MapGeoJSONFeatureWithLayer[],
        position: LngLatLike,
        clickEventListener: EventListener,
    ): void {
        this.createPopup(this.buildPopupContent(features), {
            eventListeners: {
                click: (event: Event): void => {
                    this.addActiveElementOnClick(event);
                    clickEventListener(event);
                },
            },
            maxWidth: 25,
            onClose: () => {
                if (this.isMapPresent()) {
                    return;
                }

                this.mapClickIndicatorService.removeIndicatorSource();
            },
            position: position,
        });
    }

    public createPopup(html: string, options: CreatePopupOptions): Popup {
        this.popup = new Popup({
            closeOnClick: true,
        })
            .setMaxWidth(`${options.maxWidth}rem`)
            .setLngLat(options.position)
            .setHTML(html)
            .addTo(this.map!);

        if (options.onClose) {
            this.popup.on('close', options.onClose);
        }

        this.registerPopupListeners(options);

        this.waitPopupCreated().then((): void => {
            this.detachPopupFromMap();
            this.keepPopupInsideMap();
            this.resolveOverlap(ActiveElementEnum.POPUP);
            this.enablePopupDragging();
            this.setupPopupCloseButton();
        });

        return this.popup;
    }

    public isPopupOpen(): boolean {
        return this.popup?.isOpen() ?? false;
    }

    public openSelectionPopup(
        entities: MapGeoJSONFeature[],
        position: LngLatLike,
        alreadySelectedEntityIds: readonly string[] = [],
    ): Promise<string[]> {
        return new Promise((resolve) => {
            const componentRef: ComponentRef<MapSelectionPopupComponent> = createComponent(MapSelectionPopupComponent, {
                elementInjector: this.injector,
                environmentInjector: this.environmentInjector,
            });
            componentRef.setInput('features', entities);

            const mapHeight: number = this.map!.getContainer().clientHeight;
            const maxContainerHeight: number = Math.max(
                80,
                Math.min(DEFAULT_MODAL_HEIGHT, mapHeight - POPUP_RESERVED_HEIGHT),
            );
            componentRef.setInput('maxContainerHeight', maxContainerHeight);

            componentRef.instance.initializeSelectedEntityIds(alreadySelectedEntityIds);

            const subscription = componentRef.instance.confirmedSelectedEntityIds.subscribe((entityIds) => {
                cleanup();
                resolve(entityIds);
                popup.remove();
            });

            if (this.isPopupOpen()) {
                this.popup?.remove();
            }

            const popup = this.createSelectionPopup(componentRef, position);

            popup.on('close', () => {
                cleanup();
                resolve([]);
            });

            const cleanup = (): void => {
                subscription.unsubscribe();
                this.applicationRef.detachView(componentRef.hostView);
                componentRef.destroy();
            };
        });
    }

    public removeActiveElement(): void {
        const popupContent: HTMLElement | null = this.getPopupContentElement();
        if (!popupContent) {
            return;
        }

        popupContent
            .querySelectorAll('.maplibregl-popup-link.selected-item')
            .forEach((element: Element): void => element.classList.remove(SELECTED_ITEM_SELECTOR));
    }

    public resolveClickedFeature(
        features: MapGeoJSONFeatureWithLayer[],
        markerClickEvent: Event,
    ): MapGeoJSONFeatureWithLayer | undefined {
        if (!this.isPopupOpen()) {
            return undefined;
        }

        const target = markerClickEvent.target as HTMLInputElement;
        const parentElement: HTMLLIElement | null = target.closest('li');
        const selectedLayerId: string | undefined = parentElement?.dataset?.['layerId'];
        const selectedEntityId: string | undefined = parentElement?.dataset?.['entityId'];

        return features.find(
            (feature: MapGeoJSONFeatureWithLayer) =>
                feature.properties['uuid'] === selectedEntityId && feature.source === selectedLayerId,
        );
    }

    public resolveOverlap(activeElement: ActiveElementEnum): void {
        const popupElement: HTMLElement | null = this.getPopupElement();
        if (!popupElement) {
            return;
        }

        const openedModals: OpenedModalElement[] = this.mapModalService.getOpenedElements();
        if (!openedModals.length) {
            return;
        }

        const popupRect: DOMRect = popupElement.getBoundingClientRect();
        const maxIterations: number = openedModals.length;
        let iteration = 0;

        while (iteration < maxIterations) {
            let hasResolvedOverlap = false;

            for (const modal of openedModals) {
                const modalState: ModalInstanceState = this.mapModalService.state()[modal.type];
                const modalContainer: DOMRect = modal.element.getBoundingClientRect();

                if (!this.mapElementsPositionService.areOverlapping(popupRect, modalContainer)) {
                    continue;
                }

                this.mapElementsPositionService.resolvePopupModalOverlap({
                    activeElement,
                    modalAutoMove: () => this.mapModalService.setAutoMoving(true),
                    modalElement: modal.element,
                    modalMove: (position) => this.mapModalService.moveTo(modal.type, position),
                    modalPosition: modalState.position,
                    popupElement,
                    popupMove: (dx, dy) => this.movePopupBy(dx, dy),
                });

                hasResolvedOverlap = true;
            }

            if (!hasResolvedOverlap) {
                return;
            }

            iteration++;
        }
    }

    private addActiveElementOnClick(event: Event): void {
        const popupContent: HTMLElement | null = this.getPopupContentElement();
        const link: Element | null = (event.target as HTMLElement)?.closest('.maplibregl-popup-link');

        if (!popupContent || !link) {
            this.removeActiveElement();

            return;
        }

        popupContent
            .querySelectorAll('.maplibregl-popup-link.selected-item')
            .forEach((el: Element): void => el.classList.remove(SELECTED_ITEM_SELECTOR));

        link.classList.add(SELECTED_ITEM_SELECTOR);
    }

    private buildPopupContent(features: MapGeoJSONFeatureWithLayer[]): string {
        let html = `
            <div>
                <p>${this.translocoService.translate('selectEntity', { COUNT: 1 })} (${features.length})</p>
                <ul>
        `;

        for (let i = 0; i < features.length; i++) {
            html += `
                <li data-layer-id="${features[i].source}" data-entity-id="${features[i].properties['uuid']}">
                    <a class="maplibregl-popup-link">
                        <span class="layer-name truncate">${features[i].layer.name}</span>
                        <span class="feature-property truncate">
                            ${features[i].properties['businessIdentifier'] ?? features[i].properties['uuid']}
                        </span>
                    </a>
                </li>
            `;
        }

        html += `
                </ul>
            </div>
        `;

        return html;
    }

    private createSelectionPopup(componentRef: ComponentRef<MapSelectionPopupComponent>, position: LngLatLike): Popup {
        this.applicationRef.attachView(componentRef.hostView);
        const dom: HTMLElement = componentRef.location.nativeElement;

        const popup = new Popup({ closeOnClick: false, maxWidth: EDIT_VERTEX_POPUP_WIDTH })
            .setLngLat(position)
            .setDOMContent(dom)
            .addTo(this.map!);

        this.popup = popup;

        this.waitPopupCreated().then((): void => {
            this.detachPopupFromMap();
            this.keepPopupInsideMap();
            this.resolveOverlap(ActiveElementEnum.POPUP);

            this.enablePopupDragging();
            this.setupPopupCloseButton();
        });

        return popup;
    }

    private detachPopupFromMap(): void {
        if (!this.popup || !this.map) {
            return;
        }

        const popupElement: HTMLElement | null = this.getPopupElement();
        if (!popupElement) {
            return;
        }

        const mapRect: DOMRect = this.map.getContainer().getBoundingClientRect();
        const popupRect: DOMRect = popupElement.getBoundingClientRect();

        popupElement.style.position = 'absolute';
        popupElement.style.left = `${popupRect.left - mapRect.left}px`;
        popupElement.style.top = `${popupRect.top - mapRect.top}px`;
        popupElement.style.transform = 'none';

        this.popup.setLngLat([0, 0]);
        popupElement.classList.add('popup-fixed');
    }

    private enablePopupDragging(): void {
        const popupElement: HTMLElement | null = this.getPopupElement();
        if (!popupElement) {
            return;
        }

        this.setupPopupDragHandlers(popupElement);
    }

    private getPopupContentElement(): HTMLElement | null {
        return this.popup?.getElement()?.querySelector(MAP_POPUP_SELECTOR) ?? null;
    }

    private getPopupElement(): HTMLElement | null {
        return this.popup?.getElement() ?? null;
    }

    private isClickOnExcludedSelector(pointerEvent: PointerEvent): boolean {
        return EXCLUDED_DRAGGABLE_SELECTORS.some((selector: string): Element | null =>
            (pointerEvent.target as HTMLElement)?.closest(selector),
        );
    }

    private keepPopupInsideMap(): void {
        const popupElement: HTMLElement | null = this.getPopupElement();
        if (!popupElement || !this.map) {
            return;
        }

        this.mapElementsPositionService.constrainElementInsideContainer(popupElement, this.map.getContainer());
    }

    private movePopupBy(deltaX: number, deltaY: number): void {
        const popupElement = this.getPopupElement();
        if (!popupElement) {
            return;
        }

        popupElement.style.left = `${popupElement.offsetLeft + deltaX}px`;
        popupElement.style.top = `${popupElement.offsetTop + deltaY}px`;

        this.keepPopupInsideMap();
    }

    private registerPopupListeners(options: CreatePopupOptions): void {
        if (!options.eventListeners) {
            return;
        }

        Object.entries(options.eventListeners).forEach((entry: [string, EventListener]) => {
            const event = entry[0];
            const handler = entry[1];

            this.popup?.getElement().addEventListener(event, handler);
        });
    }

    private setupPopupCloseButton(): void {
        const closeButton: Element | null | undefined = this.popup
            ?.getElement()
            ?.querySelector('.maplibregl-popup-close-button');
        if (!closeButton) {
            return;
        }

        closeButton.addEventListener('click', () => this.closePopup());
    }

    private setupPopupDragHandlers(popupElement: HTMLElement): void {
        let isDragging = false;

        let startX = 0;
        let startY = 0;

        const onPointerMove = (pointerEvent: PointerEvent): void => {
            if (!isDragging) {
                return;
            }

            popupElement.style.left = `${pointerEvent.clientX - startX}px`;
            popupElement.style.top = `${pointerEvent.clientY - startY}px`;

            this.keepPopupInsideMap();
            this.resolveOverlap(ActiveElementEnum.POPUP);
        };

        const onPointerUp = (): void => {
            isDragging = false;
            this.toggleDraggingClass(false);

            document.removeEventListener('pointermove', onPointerMove, true);
            document.removeEventListener('pointerup', onPointerUp, true);
        };

        const onPointerDown = (pointerEvent: PointerEvent): void => {
            if (this.isClickOnExcludedSelector(pointerEvent)) {
                return;
            }

            startX = pointerEvent.clientX - popupElement.offsetLeft;
            startY = pointerEvent.clientY - popupElement.offsetTop + DRAG_Y_ADJUSTMENT;

            isDragging = true;
            this.toggleDraggingClass(true);

            document.addEventListener('pointermove', onPointerMove, true);
            document.addEventListener('pointerup', onPointerUp, true);

            pointerEvent.preventDefault();
        };

        popupElement.addEventListener('pointerdown', onPointerDown);
    }

    private toggleDraggingClass(isDragging: boolean): void {
        const popupContentElement: HTMLElement | null = this.getPopupContentElement();
        if (!popupContentElement) {
            return;
        }

        popupContentElement.classList.toggle('is-dragging', isDragging);
    }

    private waitPopupCreated(): Promise<void> {
        return new Promise((resolve): void => {
            const isPopupCreated = (): void => {
                const content = this.popup?.getElement()?.querySelector(MAP_POPUP_SELECTOR);

                if (!content) {
                    requestAnimationFrame(isPopupCreated);

                    return;
                }

                resolve();
            };

            isPopupCreated();
        });
    }
}
